import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './CSHTMLConversionTemplateSetup.module.css'
import { convertCshtmlToFlatTemplate, getSampleTestData, detectModelFamily, MODEL_FAMILY_OPTIONS } from './cshtmlConverter'
import HoverButton from '../../../components/Miscellaneous/HoverButton'

const CSHTMLConversionTemplateSetup = () => {
    const navigate = useNavigate()
    // Pane 1 - import
    const [cshtmlSource, setCshtmlSource] = useState('')
    const [modelFamily, setModelFamily] = useState('auto')
    const [detectedFamily, setDetectedFamily] = useState(null)
    const [warnings, setWarnings] = useState([])
    // Pane 2 - converted output
    const [templateString, setTemplateString] = useState('')
    const [testData, setTestData] = useState('')
    // Pane 3 - preview
    const [previewHtml, setPreviewHtml] = useState('')
    const [previewError, setPreviewError] = useState('')

    const effectiveFamily = modelFamily === 'auto' ? (detectedFamily || 'other') : modelFamily

    const handleConvert = () => {
        const detected = detectModelFamily(cshtmlSource)
        setDetectedFamily(detected)
        const family = modelFamily === 'auto' ? detected : modelFamily
        const result = convertCshtmlToFlatTemplate(cshtmlSource, family)
        setTemplateString(result.templateString)
        setWarnings(result.warnings)
        setTestData(getSampleTestData(family))
        setPreviewHtml('')
        setPreviewError('')
    }

    const handlePreview = () => {
        if (!templateString) return
        try {
            let stringHolder = JSON.stringify(testData);
            const merged = templateString.replace('"%ATTENDEEINFO%"', stringHolder)
            setPreviewHtml(merged)
            setPreviewError('')
        } catch (err) {
            setPreviewError(err.message)
        }
    }

    const handlePrint = () => {
        if (!previewHtml) return
        const printWindow = window.open('', '_blank')
        printWindow.document.open()
        printWindow.document.write(previewHtml)
        printWindow.document.close()
        setTimeout(() => {
            printWindow.focus()
            printWindow.print()
            printWindow.close()
        }, 500)
    }

    const handleCopy = async text => {
        try {
            await navigator.clipboard.writeText(text)
        } catch {
            // clipboard API unavailable - the textarea content is still selectable/copyable by hand
        }
    }

    return (
        <div className="page-area">
            <h1>CSHTML to Flat Template Converter</h1>
            <button className={styles.backButton} onClick={() => navigate(-1)}> Back </button>
            <p className={styles.helpText}>
                Paste a legacy Razor (.cshtml) badge view, convert it to the flat-template JS format
                used by Flat Template Setup, then preview it against sample attendee data. This is a
                best-effort assist tool, not a guaranteed transpiler - review every <code>TODO(convert)</code> marker
                before saving the result for real use.
            </p>
            <div className={styles.threePane}>
                {/* ---------------- Pane 1: Import ---------------- */}
                <div className={styles.pane}>
                    <h2>1. Import .cshtml</h2>
                    <div className={styles.fieldRow}>
                        <label htmlFor="modelFamily" className={styles.label}>
                            Model type
                            <HoverButton
                                title={'Which legacy badge model this view was written for.\n"Auto-detect" reads it from the @model line.\nOnly affects the sample test data offered - the converter itself is generic.'}
                                messageWidth={320}
                            />
                        </label>
                        <select
                            id="modelFamily"
                            className={styles.select}
                            value={modelFamily}
                            onChange={e => setModelFamily(e.target.value)}
                        >
                            {MODEL_FAMILY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        {detectedFamily && modelFamily === 'auto' ? (
                            <span className={styles.detectedTag}>detected: {detectedFamily}</span>
                        ) : null}
                    </div>
                    <textarea
                        className={styles.sourceTextarea}
                        spellCheck="false"
                        autoCorrect="off"
                        autoCapitalize="off"
                        placeholder="Paste the full .cshtml file contents here..."
                        value={cshtmlSource}
                        onChange={e => setCshtmlSource(e.target.value)}
                        rows={30}
                    />
                    <div className={styles.buttonRow}>
                        <button className={styles.rightButton} onClick={handleConvert} disabled={!cshtmlSource.trim()}>
                            Convert
                        </button>
                    </div>
                    {warnings.length > 0 ? (
                        <div className={styles.warningsBox}>
                            <strong>Review:</strong>
                            <ul>
                                {warnings.map((w, idx) => <li key={idx}>{w}</li>)}
                            </ul>
                        </div>
                    ) : null}
                </div>

                {/* ---------------- Pane 2: Converted output ---------------- */}
                <div className={styles.pane}>
                    <h2>2. Converted template</h2>
                    <div className={styles.fieldRow}>
                        <label className={styles.label}>Flat template string</label>
                        <button className={styles.linkButton} onClick={() => handleCopy(templateString)} disabled={!templateString}>
                            Copy
                        </button>
                    </div>
                    <textarea
                        className={styles.sourceTextarea}
                        spellCheck="false"
                        autoCorrect="off"
                        autoCapitalize="off"
                        placeholder="Converted output will appear here after you click Convert. Editable - fix any TODO(convert) markers here before copying into Flat Template Setup."
                        value={templateString}
                        onChange={e => setTemplateString(e.target.value)}
                        rows={18}
                    />
                    <div className={styles.fieldRow}>
                        <label className={styles.label}>Test data (JSON array, matches "{effectiveFamily}" sample fields)</label>
                        <button className={styles.linkButton} onClick={() => handleCopy(testData)} disabled={!testData}>
                            Copy
                        </button>
                    </div>
                    <textarea
                        className={styles.sourceTextarea}
                        spellCheck="false"
                        autoCorrect="off"
                        autoCapitalize="off"
                        placeholder="Sample test data will appear here after you click Convert. Editable."
                        value={testData}
                        onChange={e => setTestData(e.target.value)}
                        rows={10}
                    />
                    <div className={styles.buttonRow}>
                        <button className={styles.rightButton} onClick={handlePreview} disabled={!templateString || !testData}>
                            Merge &amp; Preview
                        </button>
                    </div>
                    {previewError ? <div className={styles.warningsBox}>{previewError}</div> : null}
                </div>

                {/* ---------------- Pane 3: Preview ---------------- */}
                <div className={styles.pane}>
                    <h2>3. Preview</h2>
                    {previewHtml ? (
                        <>
                            <div className={styles.buttonRow}>
                                <button className={styles.button} onClick={handlePrint}>
                                    Print
                                </button>
                            </div>
                            <div className={styles.previewContainer}>
                                <iframe
                                    srcDoc={previewHtml}
                                    title="Badge Content Preview"
                                    sandbox="allow-scripts allow-modals"
                                    className={styles.previewIframe}
                                />
                            </div>
                        </>
                    ) : (
                        <div className={styles.previewPlaceholder}>
                            Convert a template on the left, then click "Merge &amp; Preview" to render it here
                            with the test data above - exactly as it will look once pasted into Flat Template Setup.
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CSHTMLConversionTemplateSetup
